import { usePersistedStore, useEphemeralStore } from '@/lib/app-store';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import {
	type DropinConfiguration,
	AdyenCheckout,
	Dropin,
	Card,
	GooglePay,
	ApplePay,
	CashAppPay,
	type PaymentMethodsResponse,
	type ResultCode,
	type Order,
	type PaymentAction,
} from '@adyen/adyen-web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/lib/i18n';
import { useLingui } from '@lingui/react/macro';
import { errorMessages } from '@/lib/portal-messages';
import { toast } from '@/lib/toast';
import { logErrorFn, extractErrorInfo } from '@/lib/data';
import { trpc, trpcClient } from '@/lib/trpc';
import { Title } from '@worldcoo/ui/components/form/title';
import { DivWithPrimaryColorVariable } from './-components/div-with-primary-color-variable';
import { useServerFn } from '@tanstack/react-start';
import { generateCaptchaToken } from '@worldcoo/ui/lib/captcha';
import { applicationType } from '@worldcoo/monolith/shared-context';
import { amountToMinorUnits } from '@worldcoo/shared/currencies';
import { localeToLanguage } from '@worldcoo/shared/core';
import { useIsMobile } from '@worldcoo/ui/hooks/use-mobile';
import adyenCssUrl from '@adyen/adyen-web/styles/adyen.css?url';

const ENABLED_GOOGLE_PAY_PORTALS = new Set(['glovo', 'telefonica', 'rodilla']);

const ADYEN_HOVER_PRELOADS: Array<{ rel: string; href: string; as?: string }> = [
	{ rel: 'preconnect', href: 'https://checkoutshopper-live.adyen.com' },
	{ rel: 'dns-prefetch', href: 'https://checkoutshopper-live.adyen.com' },
	{ rel: 'preload', href: adyenCssUrl, as: 'style' },
];

// Intent preload only fetches the route chunk; head() links are injected on
// match. Mirror the Adyen hints into <head> from the loader so hovering the
// "Next" button also warms the CDN connection and the stylesheet.
function injectAdyenHoverPreloads() {
	if (typeof document === 'undefined') return;
	for (const { rel, href, as } of ADYEN_HOVER_PRELOADS) {
		if (document.head.querySelector(`link[rel="${rel}"][href="${href}"]`)) continue;
		const link = document.createElement('link');
		link.rel = rel;
		link.href = href;
		if (as) link.as = as;
		document.head.appendChild(link);
	}
}

export const Route = createFileRoute('/$portalId/$campaignId/donate/checkout')({
	loader: () => {
		injectAdyenHoverPreloads();
	},
	head: () => ({
		meta: [{ name: 'robots', content: 'noindex' }],
		links: [...ADYEN_HOVER_PRELOADS, { rel: 'stylesheet', href: adyenCssUrl }],
	}),
	component: RouteComponent,
});

function RouteComponent() {
	const { portalId, campaignId } = Route.useParams();
	const navigate = Route.useNavigate();
	const queryClient = useQueryClient();
	const hasHydrated = usePersistedStore((state) => state._hasHydrated);
	const amount = usePersistedStore((state) => state.amount);
	const details = usePersistedStore((state) => state.details);
	const { t } = useLingui();
	const { language } = useTranslation();
	const { mutateAsync: preregisterDonation } = useMutation(trpc.payments.preregisterDonation.mutationOptions());
	const { mutateAsync: getPaymentDetails } = useMutation(trpc.payments.getPaymentDetails.mutationOptions());
	const { mutateAsync: processPayment } = useMutation(trpc.payments.processPayment.mutationOptions());
	const dropinContainerRef = useRef<HTMLDivElement>(null);
	const termsContent = usePersistedStore((state) => state.termsContent);
	const ngosTermsContent = usePersistedStore((state) => state.ngosTermsContent);
	const telefonicaTermsContents = usePersistedStore((state) => state.telefonicaTermsContent);
	const setDonationToConfirm = useEphemeralStore((store) => store.setDonationToConfirm);
	const cachedPreregistration = useEphemeralStore((state) => state.cachedPreregistration);
	const getCachedPaymentMethods = useEphemeralStore((state) => state.getCachedPaymentMethods);
	const logError = useServerFn(logErrorFn);
	const isMobile = useIsMobile();

	const currencyCode = 'EUR'; // TODO!!!

	useEffect(() => {
		if (hasHydrated && !amount) navigate({ to: `/$portalId/$campaignId/donate`, params: { portalId, campaignId } });
	}, [navigate, portalId, campaignId, hasHydrated, amount]);

	useEffect(() => {
		console.time('Adyen Checkout initialization');
		const initAdyenCheckout = async () => {
			// Use cached preregistration if available, otherwise create new one
			const preloadPreregisterDonation =
				cachedPreregistration && cachedPreregistration.captchaPassed
					? Promise.resolve(cachedPreregistration)
					: generateCaptchaToken().then((captchaToken) => {
							if (!amount) return;

							return preregisterDonation({
								certificated: details.certificate,
								amount: amount,
								campaignId,
								currency: currencyCode,
								applicationId: {
									type: applicationType.portal,
									host: portalId,
								},
								donor: {
									name: details.firstname,
									lastName: details.lastname,
									contact: {
										email: details.email,
										language: localeToLanguage[language],
										physicalAddress: {
											region: details.region,
											street: {
												name: details.streetName,
												number: details.streetNumber,
												type: '',
											},
											additionalInfo: details.additionalInfo,
											city: details.city,
											country: details.country,
											postalCode: details.cp,
											province: details.province,
										},
									},
									document: {
										number: details.documentNumber,
										type: details.documentType,
									},
									subscribeNewsletter: false, // TODO: we should ask for this?
								},
								checkbox: [
									termsContent && { type: 'termsAndConditions' as const, ...termsContent },
									ngosTermsContent && { type: 'ngoTermsAndConditions' as const, ...ngosTermsContent },
									telefonicaTermsContents && {
										type: 'telefonicaTermsAndConditions' as const,
										...telefonicaTermsContents,
									},
								].filter((value) => value !== undefined),
								captchaToken,
							});
						});

			if (!amount) return;

			// Use cached payment methods if available and not expired, otherwise fetch new ones
			const cached = getCachedPaymentMethods();
			const paymentMethods =
				cached && (cached as { captchaPassed?: boolean }).captchaPassed
					? cached
					: await generateCaptchaToken().then((captchaToken) => {
							return trpcClient.payments.getPaymentMethods.mutate({
								amount: { value: amountToMinorUnits(amount, currencyCode), currency: currencyCode },
								countryCode: details.country,
								locale: language,
								captchaToken,
							});
						});

			if (!(paymentMethods as { captchaPassed?: boolean }).captchaPassed) {
				toast.error(t(errorMessages.captchaFailed));
				return;
			}

			const checkout = await AdyenCheckout({
				locale: language === 'es' ? 'es-ES' : language === 'ca' ? 'ca-ES' : language === 'it' ? 'it-IT' : 'en-US',
				countryCode: details.country,
				environment: import.meta.env.VITE_ADYEN_CLIENT_KEY!.startsWith('test_') ? 'test' : 'live',
				clientKey: import.meta.env.VITE_ADYEN_CLIENT_KEY,
				paymentMethodsResponse: paymentMethods as PaymentMethodsResponse,
				amount: { value: amountToMinorUnits(amount, currencyCode), currency: currencyCode },
				onSubmit: async (state, _component, actions) => {
					try {
						const preregisterDonationResult = await preloadPreregisterDonation;

						if (!preregisterDonationResult?.captchaPassed) {
							toast.error(t(errorMessages.captchaFailed));
							actions.reject();
							return;
						}

						const { donationId, paymentToken } = preregisterDonationResult;

						const result = await processPayment({
							donationId,
							amount: {
								currency: currencyCode,
								value: amountToMinorUnits(amount, currencyCode),
							},
							paymentMethodData: state.data,
							returnUrl: window.location.origin,
							paymentToken,
							captchaToken: await generateCaptchaToken(),
						});

						const { resultCode, action, order, donationToken } = result;

						// If the /payments request request form your server is successful, you must call this to resolve whichever of the listed objects are available.
						// You must call this, even if the result of the payment is unsuccessful.
						actions.resolve({
							resultCode: resultCode as ResultCode,
							action: action as PaymentAction,
							order: order as Order,
							donationToken,
						});
					} catch (error) {
						console.error('onSubmit', error);
						const errorInfo = extractErrorInfo(error);
						logError({
							data: {
								route: window.location.pathname,
								name: '[ADYEN CHECKOUT] onSubmit: ' + errorInfo.name,
								message: errorInfo.message,
								stack: errorInfo.stack,
							},
						});
						actions.reject();
					}
				},
				onAdditionalDetails: async (state, _component, actions) => {
					try {
						const result = await getPaymentDetails({
							paymentDetailsData: state.data,
							captchaToken: await generateCaptchaToken(),
						});

						if (!result.captchaPassed) {
							toast.error(t(errorMessages.captchaFailed));
							actions.reject();
							return;
						}

						const { resultCode, action, order, donationToken } = result;

						// If the /payments/details request request from your server is successful, you must call this to resolve whichever of the listed objects are available.
						// You must call this, even if the result of the payment is unsuccessful.
						actions.resolve({
							resultCode: resultCode as ResultCode,
							action: action as PaymentAction,
							order: order as Order,
							donationToken,
						});
					} catch (error) {
						console.error('onAdditionalDetails', error);
						const errorInfo = extractErrorInfo(error);
						logError({
							data: {
								route: window.location.pathname,
								name: '[ADYEN CHECKOUT] onAdditionalDetails: ' + errorInfo.name,
								message: errorInfo.message,
								stack: errorInfo.stack,
							},
						});
						actions.reject();
					}
				},
				onPaymentCompleted: async (result, component) => {
					const preregisterDonationResult = await preloadPreregisterDonation;
					if (!preregisterDonationResult?.captchaPassed) {
						toast.error(t(errorMessages.captchaFailed));
						return;
					}

					console.info(result, component);

					if (preregisterDonationResult?.donationId) {
						setDonationToConfirm({ id: preregisterDonationResult.donationId, campaignId });
					}

					navigate({ to: '/$portalId/$campaignId/donate/thanks', params: { portalId, campaignId } });
				},
				onPaymentFailed: (result, component) => {
					console.info(result, component);
					logError({
						data: {
							route: window.location.pathname,
							name: '[ADYEN CHECKOUT] onPaymentFailed',
							message: JSON.stringify(result),
							stack: undefined,
						},
					});
				},
				onError: (error, component) => {
					const errorInfo = extractErrorInfo(error);
					// Apple Pay sessions are cancelled by the user, not by an error, but Adyen calls onError in that case with a message that includes "ApplePay UI dismissed", so we can just ignore those errors
					if (errorInfo.message.includes('ApplePay UI dismissed')) return;

					toast.error(errorInfo.message, { duration: 15_000 });
					console.error(errorInfo.name, errorInfo.message, errorInfo.stack, component);
					logError({
						data: {
							route: window.location.pathname,
							name: '[ADYEN CHECKOUT] onError: ' + errorInfo.name,
							message: errorInfo.message,
							stack: errorInfo.stack,
						},
					});
				},
			});

			const paymentMethodComponents: DropinConfiguration['paymentMethodComponents'] = [];

			if (isMobile) {
				paymentMethodComponents.push(ApplePay);

				if (ENABLED_GOOGLE_PAY_PORTALS.has(portalId)) {
					paymentMethodComponents.push(GooglePay);
				}
			}

			paymentMethodComponents.push(Card, CashAppPay);

			new Dropin(checkout, {
				paymentMethodComponents,
				paymentMethodsConfiguration: {
					card: {
						hasHolderName: true,
						holderNameRequired: true,
						enableStoreDetails: true,
						hideCVC: false,
					},
				},
			}).mount(dropinContainerRef.current!);

			console.timeEnd('Adyen Checkout initialization');
		};

		initAdyenCheckout();
	}, [
		amount,
		campaignId,
		details.additionalInfo,
		details.certificate,
		details.city,
		details.country,
		details.cp,
		details.documentNumber,
		details.documentType,
		details.email,
		details.firstname,
		details.lastname,
		details.province,
		details.region,
		details.streetName,
		details.streetNumber,
		getPaymentDetails,
		language,
		navigate,
		ngosTermsContent,
		preregisterDonation,
		processPayment,
		queryClient,
		portalId,
		telefonicaTermsContents,
		termsContent,
		setDonationToConfirm,
		cachedPreregistration,
		getCachedPaymentMethods,
		t,
		logError,
		isMobile,
	]);

	return (
		<>
			<Title size="lg" className="text-center">
				{t`Payment method`}
			</Title>

			<DivWithPrimaryColorVariable className="min-h-107">
				<div ref={dropinContainerRef} />
			</DivWithPrimaryColorVariable>

			<p className="text-muted-foreground/50 mx-auto mt-auto flex max-w-md gap-2 text-center text-sm leading-none font-medium whitespace-pre-line">
				{t`protected by reCAPTCHA.`}
				<a href="https://policies.google.com/privacy" className="underline" target="_blank" rel="noreferrer noopener">
					{t`Privacy`}
				</a>
				<a href="https://policies.google.com/terms" className="underline" target="_blank" rel="noreferrer noopener">
					{t`Terms`}
				</a>
			</p>
		</>
	);
}
